import { z } from 'zod'
import * as m from '~/paraglide/messages'

const coordinatesSchema = z.object({
  lat: z.number(),
  lng: z.number(),
})

const locationSchema = z.object({
  name: z.string().nullable().optional(),
  street: z.string().min(1, m.streetRequired()),
  city: z.string().min(1, m.cityRequired()),
  country: z.string().min(1, m.countryRequired()),
  coordinates: coordinatesSchema.optional(),
})

/**
 * Business as exposed to the dashboard - mirrors the `business` field of
 * `/api/businesses/me` (see businessMeResponseSchema). This is the REAL
 * business record: the legal entity backing one or more listings, with
 * trading + legal names + subscription state.
 *
 * Listing-shaped fields (display name, description, hours, location, ...)
 * intentionally do NOT live on this type. Pages that edit those fields
 * use {@link usePrimaryListing} from hooks/use-listings instead - they're
 * editing a listing, not the business row. The two used to be conflated
 * via `Business = Listing` which produced the "My Business" placeholder
 * + "123 Main St" defaults bug; the type split makes that mistake harder
 * to reintroduce.
 */
export interface Business {
  id: string
  companyName: string
  legalName: string
  registrationNumber: string | null
  vatId: string | null
  companyDetailsPending: boolean
  hasProfile: boolean
}

/** Form-level update schemas - these still target the primary LISTING, not
 *  the business. The form sections share these schemas with the create-listing
 *  flow; the listing PUT validates on the server.
 */
export const updateBasicInfoSchema = z.object({
  name: z.string().min(1, m.businessNameRequired()),
  description: z.string().min(10, m.descriptionMustBeAtLeast10Characters()),
})

export const updateContactSchema = z.object({
  phone: z.string().optional(),
  email: z.string().optional(),
  website: z.string().optional(),
})

export const updateLocationSchema = locationSchema.omit({ coordinates: true }).extend({
  coordinates: coordinatesSchema.optional(),
})

export const updateSocialLinksSchema = z.object({
  facebook: z.url(m.invalidUrl()).optional().or(z.literal('')),
  instagram: z.url(m.invalidUrl()).optional().or(z.literal('')),
  twitter: z.url(m.invalidUrl()).optional().or(z.literal('')),
})
